import { inject, Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from "@angular/router";
import {
  AuthService,
  POST_AUTH_RETURN_URL_STATE_KEY,
} from "./auth.service";
import { MatDialog } from "@angular/material/dialog";
import { ToastService } from "src/app/services/toast-service/toast.service";
import { TranslateService } from "@ngx-translate/core";

@Injectable({ providedIn: "root" })
export class AuthGuard {
  #authService = inject(AuthService);
  #toastService = inject(ToastService);
  #router = inject(Router);
  #translate = inject(TranslateService);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const currentUser = this.#authService.currentUserValue;

    if (!currentUser) {
      // Persist to sessionStorage (not just the query param) so the deep
      // link survives a subsequent SSO login's hard page navigation — see
      // AuthService.persistReturnUrl/takeReturnUrl for the full rationale.
      this.#authService.persistReturnUrl(state.url);
      this.#toastService.toastError(
        this.#translate.instant("AUTH.UNAUTHORIZED_ALERT"),
      );
      return this.#router.createUrlTree(["/auth/login"], {
        queryParams: { returnUrl: state.url },
      });
    }

    if (!this.#authService.getSelectedRole()) {
      this.#authService.persistReturnUrl(state.url);
      return this.#router.createUrlTree(["/select-role"], {
        queryParams: { returnUrl: state.url },
      });
    }

    const roles = this.getRouteRoles(route);

    if (Array.isArray(roles)) {
      const selectedRoleId = this.#authService.getActiveRoleId();

      if (selectedRoleId == null || !roles.includes(selectedRoleId)) {
        const fallbackUrl = this.getPostAuthFallbackUrl(state.url);

        if (fallbackUrl != null) {
          return this.#router.createUrlTree([fallbackUrl]);
        }

        return this.#router.createUrlTree(["/error/403"]);
      }
    }

    // if (permissions) {
    //   return this.#permissionsService
    //     .hasPermission(permissions)
    //     .then((ok) => ok || this.#router.createUrlTree(["/403"]));
    // }
    return true;
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.canActivate(route, state);
  }

  private getPostAuthFallbackUrl(attemptedUrl: string): string | null {
    const postAuthReturnUrl = this.#router.currentNavigation()?.extras.state?.[
      POST_AUTH_RETURN_URL_STATE_KEY
    ];

    if (postAuthReturnUrl !== attemptedUrl) {
      return null;
    }

    const fallbackUrl = this.#authService.getDefaultAuthenticatedUrl();

    if (fallbackUrl === attemptedUrl || fallbackUrl === "/error/403") {
      return null;
    }

    return fallbackUrl;
  }

  private getRouteRoles(route: ActivatedRouteSnapshot): unknown {
    let roles = route.data["roles"];

    while (route.firstChild) {
      route = route.firstChild;
      roles = route.data["roles"] ?? roles;
    }

    return roles;
  }
}
